import { MoreVertOutlined, Search, SortByAlpha } from '@mui/icons-material'
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import LastPageIcon from '@mui/icons-material/LastPage'
import React, { ChangeEvent, CSSProperties, memo, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  checkRolePermissionOnLinkClick,
  getDynamicRowValue,
  getFilterOptions,
  getListingRowData,
  getRoleByURL,
  getSessionStorage,
  getSessionStorageForRoute,
  removeSessionStorage,
  replaceUnwanted,
  setSessionStorage,
} from '../../../Helpers/Util'
import useDebounce from '../../../Hooks/useDebounce'
import useScrollToRow from '../../../Hooks/useScrollToRow'
import { UserMuiTableModal } from '../../../Models/ComponentModel'
import {
  BackButton,
  MUIButton,
  MUIFieldWithoutControl,
  MUIGrid,
  MUIPagination,
  SimpleDropdown,
} from '../../Shared'
import { DescendingIcon, NoAssetIcon } from '../../../Components/Icons'
import CloneModal from '../../Shared/CloneModal'
import ListingFilter from '../../Atoms/ListingFilter'
import CONSTANTS, { PAGES_ROUTES } from '../../../Constants'
import mainApiService from '../../../Services'
import {
  AUDIT_LOGS,
  CONTAINER_MANAGEMENT_CRUD,
  SITE_NAVIGATION_CRUD,
  WIDGET_MANAGEMENT_CRUD,
} from '../../../Constants/ApiDefinations'
import VersionIcon from '../../../Assets/Images/svgs/version-icon'
import ProgressBarIcon from '../../../Assets/Images/svgs/progress-bar-icon'
import { TooltipText } from '../../Shared/TooltipText/TooltipText'
import SortIcon from '@mui/icons-material/Sort'
import { defaultFilterConditionValue } from '../../../Constants/Constant/ListingPageConstant/ContentListingConstants'
import MUIDateTimePicker from '../../MUIDateTimePicker'
import ProgressBar from '../../Shared/ProgressBar'
import { MODULE_CONSTANTS } from '../../../Constants/Texts'
import Caution from '../../../Assets/Images/svgs/caution-icon'
import CopyTextTooltip from '../../Shared/CopyTextTooltip'
import MUICheckbox from '../../Shared/MUI-Checkbox'
import { EyeIcon } from '../../../Assets/Images/svgs'
import { PageNames } from '../../../Constants/PagesRoutes/PageURLs'
import TrayRankingStatusChip from '../../Labels/TrayRankingStatusChip'
import { containerTrayRankingDefinitionKey } from '../../../Helpers/constant'
import { logger } from '../../../Helpers'

/**
 * ListingComponent is a reusable component that displays a list of items.
 *
 * @param {Object} props - The props object for the component.
 * @param {Object} props.cloneProps - Properties for cloning functionality.
 * @param {boolean} props.cloneProps.isClonable - Whether the items can be cloned (true/false).
 * @param {string} props.cloneProps.cloneSegmentIdNode - ID node for clone API payload (e.g., 'parameterId').
 * @param {string} props.cloneProps.apiPayloadIdNode - Exact ID node name required in payload as per Swagger (e.g., 'parameterId', 'segmentId').
 * @param {string} props.cloneProps.apiMidpoint - API endpoint midpoint for cloning as per Swagger(e.g., 'parameter').
 * @param {string} props.cloneProps.redirectViewApiMidpoint - API endpoint midpoint for viewing cloned items (e.g., 'parameter').
 * @param {string} props.cloneProps.titleNode - Node name for title in API response as per Swagger  API response(e.g., 'parameterName', 'widgetTitle').
 */

const ListingComponent: React.FC<UserMuiTableModal> = ({
  advancedSearch = false,
  showTable = true,
  columns,
  tableRows = [],
  tableTitle,
  headerBtnLabel = 'Add new',
  headerBtnVariant = 'contained',
  createButtonLabel = 'Create',
  createButtonClick,
  searchFunction,
  handleRowClick,
  paginationOption = [],
  dataCount = 0,
  searchPlaceholder = 'Search',
  showCreateButton = true,
  showSearchField = true,
  isRowClickable = true,
  isCellClickable = true,
  showBackButton = false,
  emptyStateText,
  cloneToDisable = '',
  cloneProps = {
    isClonable: false,
    cloneSegmemt: '',
    apiMidPoint: '',
    redirectViewAPiRouteMidPoint: '',
    titleNodeToDisplay: '',
    title: '',
    openInNewTab: false,
  },
  handelBackButton,
  showHeaderInfo = [],
  searchLabel = '',
  showSearchIcon = true,
  searchFullWidth = false,
  showFilterOptions = false,
  filterFields = [],
  headerAddon,
  parentName = '',
  lastSearchFieldValue = '',
  setLastSearchFieldValue,
  searchClassName = '',
  searchBarSize = '',
  showVersionColumn = false,
  entityType = 'widget',
  resetPageNumber = false,
  initialCheckedState,
  noDataPlaceHolder = 'No Data Found',
  showAdvanceFilter = false,
  isAssociatedList = false,
  clearFilterValues,
  filterValuesCleared,
  isLoading = false,
  isProgressBar = false,
  shouldSearchFunctionCall = false,
  selectAllProps = {},
  onSelectAllChange,
  isRetainFilters = false,
  isNestedRows = false,
  isCopyActionAllowed = false,
  copyableColumns = [],
  onFetchOlderRecords,
  hideFetchOlderRecords = false,
  noPadding = false,
  noSearchResultText = '',
  isActiveWindow = '',
  createButtonDisabled = false,
  handleClearAllFilters,
  listingContainerParentClassName = '',
  entryPoint = '',
  displayMode = 'modal',
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const stateParam = searchParams.get('state')
  const routeData = isRetainFilters ? getSessionStorageForRoute(location.pathname) : {}
  const hasRouteData = Object.keys(routeData).length > 0
  const localRoute = getSessionStorage('route')
  const shouldApplyFilter = isRetainFilters && (hasRouteData || localRoute === location.pathname)
  const getFilterValue = (routeValue: any, sessionKey: string) => {
    if (hasRouteData) return routeValue
    if (shouldApplyFilter) return getSessionStorage(sessionKey)
    return undefined
  }

  const localFilters = getFilterValue(routeData.localFilters, 'localFilters')
  const localPageRaw = getFilterValue(routeData.localPageData, 'localPageData')
  const { localPageCount, localPageNumber, scrollPosition } = localPageRaw || {}

  const getLocalSearchFilter = () => {
    const searchParam = searchParams.get('search')
    if (searchParam) return searchParam
    if (hasRouteData) return routeData.localSearchFilter ?? ''
    if (shouldApplyFilter) return getSessionStorage('localSearchFilter') ?? ''
    return ''
  }
  const localSearchFilter = getLocalSearchFilter()

  const [pageData, setPageData] = useState({
    pageCount: shouldApplyFilter ? (localPageCount ?? 10) : 10,
    pageNumber: shouldApplyFilter ? (localPageNumber ?? 1) : 1,
  })
  const [colData, setColData] = useState({
    selectedCol: '',
    selectedOptionType: 0,
  })
  const [anchorEl, setAnchorEl] = useState(null)
  const [anchorElFilter, setAnchorElFilter] = useState(null)
  const [searchInput, setSearchInput] = useState<string>(shouldApplyFilter ? localSearchFilter : '')
  const [selectedId, setSelectedId] = useState<number | string>('')
  const [selectedCol, setSelectedCol] = useState({
    selectedCol: '',
    selectedOptionType: 0,
  })
  const [selectedVersion, setSelectedVersion] = useState<any[]>([])
  const [selectedVersionId, setSelectedVersionId] = useState([])
  const [showVersions, setShowVersions] = useState(false)
  const [condition, setCondition] = useState(defaultFilterConditionValue)
  const [openFilter, setOpenFilter] = useState(false)
  const [filterInput, setFilterInput] = useState<string>('')
  const [selectedDateTime, setSelectedDateTime] = useState<any>()
  const [filters, setFilters] = useState<any[]>(
    localFilters && shouldApplyFilter ? localFilters : [],
  )
  const [showProgressBar, setShowProgressBar] = useState(false)
  const [selectedProgressBarId, setSelectedProgressBarId] = useState('')
  const [progressBarData, setProgressBarData] = useState<any>({})
  const [shouldListApiCall, setShouldListApiCall] = useState(true)
  const [headerColumns, setHeaderColumns] = useState<any[]>([])
  const [selectAllCheckboxData, setSelectAllCheckboxData] = useState<any>({})
  const [copiedText, setCopiedText] = useState('')
  const [clearFiltersTrigger, setClearFiltersTrigger] = useState(0)

  const searchValue = useDebounce(searchInput, 1000)

  useScrollToRow({
    scrollPosition,
    tableRows,
  })

  useEffect(() => {
    if (isRetainFilters) {
      if (searchParams.get('search') && !getSessionStorage('localSearchFilter')) {
        setSessionStorage('localSearchFilter', searchParams.get('search'), location.pathname)
      }
    }
    if (lastSearchFieldValue) {
      setSearchInput(lastSearchFieldValue)
      if (isRetainFilters) {
        setSearchParams({ search: lastSearchFieldValue }, { replace: true })
        setSessionStorage('localSearchFilter', lastSearchFieldValue, location.pathname)
      }
    }
  }, [])
  useEffect(() => {
    if (Object.keys(selectAllProps).length === 0) return
    setSelectAllCheckboxData(selectAllProps)
  }, [selectAllProps])
  useEffect(() => {
    const filteredColumns = columns?.filter((column) => column.label.trim() !== '') || []
    setHeaderColumns(selectAllCheckboxData?.showCheckbox ? filteredColumns : (columns ?? []))
  }, [columns, selectAllCheckboxData?.showCheckbox])
  useEffect(() => {
    setShouldListApiCall(shouldSearchFunctionCall)
  }, [shouldSearchFunctionCall])

  useEffect(() => {
    setShowVersions(selectedVersion.length > 0)
  }, [selectedVersion])

  useEffect(() => {
    setShowProgressBar(Object.keys(progressBarData).length > 0)
  }, [progressBarData])

  useEffect(() => {
    if (resetPageNumber) {
      setPageData((pre) => ({ ...pre, pageNumber: 1 }))
      setLocalFiltersAndPageData(undefined, {
        localPageCount: pageData.pageCount,
        localPageNumber: 1,
        scrollPosition: 0,
      })(false)
    }
  }, [resetPageNumber])

  useEffect(() => {
    if (clearFilterValues) {
      setFilterInput('')
      setSearchInput('')
      isRetainFilters && setSearchParams({}, { replace: true })
      isRetainFilters && setSessionStorage('localFilters', null, location.pathname)
      isRetainFilters && setSessionStorage('localSearchFilter', null, location.pathname)
      setFilters([])
      setSelectedCol({
        selectedCol: '',
        selectedOptionType: 0,
      })
      if (filterValuesCleared) filterValuesCleared(true)
    }
  }, [clearFilterValues])
  useEffect(() => {
    isRetainFilters && setPageData((prev) => ({ ...prev, pageNumber: localPageNumber ?? 1 }))
    if (isNestedRows) {
      setSelectedVersion(tableRows)
    }
  }, [tableRows, location])

  useEffect(() => {
    setShouldListApiCall(true)
  }, [searchValue])
  useEffect(() => {
    if (!shouldListApiCall) return
    const column =
      selectedCol.selectedCol === 'stateDisplayName' ? 'state' : selectedCol.selectedCol
    const columnValue = showFilterOptions ? selectedCol.selectedCol : column
    const filter = showFilterOptions ? filters : { filterBY: '', filterText: '' }

    if (searchFunction)
      searchFunction(
        searchValue,
        selectedCol.selectedOptionType,
        columnValue,
        filter,
        pageData.pageNumber,
        pageData.pageCount,
      )
    setShouldListApiCall(false)
  }, [shouldListApiCall])

  useEffect(() => {
    if (copiedText) {
      const timer = setTimeout(() => {
        setCopiedText('')
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [copiedText])

  const openFilterInSort = (e: any) => {
    setAnchorElFilter(e.currentTarget)
    setOpenFilter(true)
  }
  const closeFilterInSort = () => {
    setOpenFilter(false)
    setAnchorElFilter(null)
  }
  const handleConditionChange = (event: any) => {
    setCondition(event?.target.value)
  }
  const handleFilterInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFilterInput(event?.target.value)
  }
  const handleFilterDateChange = (date: any) => {
    setSelectedDateTime(date)
  }

  const handleSortIconClick = (event: React.MouseEvent<HTMLSpanElement>, colName: string) => {
    if (!columns) return
    const filterValueType = columns.find((col: { id: any }) => col.id === colName)?.dataType
    getFilterOptions(filterValueType ? [filterValueType] : [])
    setPreviousSelectedFilterValue(colName, columns)
    event.preventDefault()
    if (selectedCol.selectedCol === colName) {
      setColData(() => ({
        ...selectedCol,
        selectedCol: colName,
      }))
    } else {
      setColData((pre) => ({
        ...pre,
        selectedCol: colName,
        selectedOptionType: 0,
      }))
    }
    setAnchorEl(event.currentTarget as any)
  }

  const handleApply = (col: any) => {
    closeFilterInSort()
    onMenuClose(true)
    const obj = {
      filterBy: colData.selectedCol,
      filterCondition: condition,
      filterValue:
        col.dataType === 'date' ? new Date(selectedDateTime).toISOString() : filterInput.trim(),
    }
    handleFilterSubmit(obj, colData.selectedCol, true)
  }

  const setPreviousSelectedFilterValue = (col: any, cols: any) => {
    const previousFilters = Array.isArray(filters)
      ? filters.filter((filter) => filter.filterBy === col)
      : []
    const colDatatype = cols.find((filter: any) => filter.id === col)
    if (previousFilters.length) {
      setCondition(previousFilters[0].filterCondition)
      if (colDatatype.dataType === 'date') {
        setSelectedDateTime(new Date(previousFilters[0].filterValue))
      } else {
        setFilterInput(previousFilters[0].filterValue)
      }
    }
  }

  const resetPageData = () => {
    setSessionStorage(
      'localPageData',
      {
        localPageCount: pageData.pageCount,
        localPageNumber: 1,
        scrollPosition: 0,
      },
      location.pathname,
    )
  }

  const setLocalFiltersAndPageData = (filters?: any, pageData?: any) => {
    return (excludeSort?: boolean) => {
      if (filters && !excludeSort) {
        isRetainFilters && setSessionStorage('localFilters', filters, location.pathname)
        isRetainFilters && resetPageData()
      }
      if (pageData && !excludeSort) {
        isRetainFilters && setSessionStorage('localPageData', pageData, location.pathname)
      }
    }
  }

  const onMenuClose = (clearCol = false) => {
    setAnchorEl(null)
    if (clearCol) {
      setColData({
        selectedCol: '',
        selectedOptionType: 0,
      })
    }
    setCondition(defaultFilterConditionValue)
    setFilterInput('')
    setSelectedDateTime('')
  }

  const handleCellData = (isClickToCopyEnabled: boolean, value: any, columnValue: any) => {
    return (
      <div
        className={`${isCellClickable ? 'cursor-pointer' : ''} paragraph-regular-6 interFont`}
        data-testid='tablerow'
      >
        {isClickToCopyEnabled ? (
          <CopyTextTooltip child={displayValue(value, columnValue)} />
        ) : (
          displayValue(value, columnValue)
        )}
      </div>
    )
  }
  const tablePaginationChange = (event: React.ChangeEvent<unknown>, page: number) => {
    event.preventDefault()
    setShowProgressBar(false)
    setPageData((pre) => ({ ...pre, pageNumber: page }))
    setLocalFiltersAndPageData(null, {
      localPageCount: pageData.pageCount,
      localPageNumber: page,
      scrollPosition: 0,
    })(false)
    setShouldListApiCall(true)
  }

  const handlePerPageView = (e: any) => {
    e.preventDefault()
    setPageData((pre) => ({ ...pre, pageNumber: 1, pageCount: e.target.value }))
    setLocalFiltersAndPageData(null, {
      localPageCount: e.target.value,
      localPageNumber: 1,
      scrollPosition: 0,
    })(false)
    setShouldListApiCall(true)
  }

  const handleRefresh = () => {
    setShouldListApiCall(true)
  }

  const handleFilterSubmit = (filter: any, id: string, excludeSort?: boolean) => {
    const isFilterEmpty = Object.keys(filter).length === 0

    if (isFilterEmpty) {
      handleEmptyFilter(id, excludeSort)
    } else {
      handleNonEmptyFilter(filter, id, excludeSort)
    }
    if (pageData.pageNumber > 1) {
      setPageData((prev) => ({ ...prev, pageNumber: 1 }))
    }
    setShouldListApiCall(true)
  }

  const handleEmptyFilter = (id: string, excludeSort?: boolean) => {
    if (filters.length > 0) {
      const updatedFilters = filters.filter((filter) => filter.filterBy !== id)
      setFilters(updatedFilters)
      setLocalFiltersAndPageData(updatedFilters)(excludeSort)
    } else {
      setFilters([])
      isRetainFilters && setSessionStorage('localFilters', null, location.pathname)
    }
  }

  const handleNonEmptyFilter = (filter: any, id: string, excludeSort?: boolean) => {
    const filterExists = Array.isArray(filters)
      ? filters.some((item) => item.filterBy === id)
      : false

    if (filters.length === 0) {
      setFilters([filter])
      setLocalFiltersAndPageData([filter])(excludeSort)
    } else if (filterExists) {
      const updatedFilters = filters.map((item) => (item.filterBy === id ? filter : item))
      setFilters(updatedFilters)
      setLocalFiltersAndPageData(updatedFilters)(excludeSort)
    } else {
      setFilters((prev) => [...prev, filter])
      setLocalFiltersAndPageData([...filters, filter])(excludeSort)
    }
  }

  const clearFilter = (id: string) => {
    const updatedFilters = filters.filter((val) => val.filterBy !== id)
    setAnchorEl(null)
    setShouldListApiCall(true)
    setFilters(updatedFilters)
    setCondition(defaultFilterConditionValue)
    setFilterInput('')
    setSelectedDateTime('')
  }

  const checkIfFilterApplied = (id: string) => {
    return Array.isArray(filters) && filters?.filter((val) => val.filterBy === id).length > 0
  }

  const handleVersionIconClick = async (row: any) => {
    if (selectedVersionId === row._id) {
      setShowVersions(!showVersions)
      return
    }

    const selectedParameters = columns?.map((column: any) => column?.id)

    const compiledData = getPayloadForEntityType(entityType, row)
    const payload = {
      ...compiledData,
      selectedParameters: selectedParameters,
    }
    if (!payload) return

    const requestedData = getRequestedData(entityType, payload, row)
    if (!requestedData) return

    const result = await fetchVersionData(requestedData)
    if (result) {
      const transformedData = await processResultData(result?.data)
      setSelectedVersionId(row._id)
      setSelectedVersion(transformedData)
    }
  }

  const preparePayload = (row: any) => {
    const filters = [
      {
        filterBy: 'contentDocId',
        filterCondition: 'equals',
        filterValue: row.documentId,
      },
    ]

    return {
      filters,
    }
  }

  const getPayloadForEntityType = (entityType: string, row: any) => {
    switch (entityType) {
      case 'widget':
        return { widgetId: row.widgetId, _id: row._id }
      case 'container':
        return { containerId: row.containerId, _id: row._id }
      case 'siteNavigation':
        return { siteNavigationId: row.siteNavigationId, _id: row._id }
      case 'advanceSearch':
        return getAdvanceSearchPayload(row)
      default:
        return null
    }
  }

  const getAdvanceSearchPayload = (row: any) => {
    const { contentType, contentId, _id } = row
    if (contentType === 'container') return { containerId: contentId, _id }
    if (contentType === 'widget') return { widgetId: contentId, _id }
    if (contentType === 'site-navigation') return { siteNavigationId: contentId, _id }
    return null
  }

  const getRequestedData = (entityType: string, payload: any, row: any) => {
    switch (entityType) {
      case 'widget':
        return WIDGET_MANAGEMENT_CRUD.VERSION_LIST(payload)
      case 'container':
        return CONTAINER_MANAGEMENT_CRUD.VERSION_LIST(payload)
      case 'siteNavigation':
        return SITE_NAVIGATION_CRUD.VERSION_LIST(payload)
      case 'advanceSearch':
        return getAdvanceSearchRequestedData(row, payload)
      default:
        return null
    }
  }

  const getAdvanceSearchRequestedData = (row: any, payload: any) => {
    delete payload.selectedParameters
    if (row.contentType === 'container') return CONTAINER_MANAGEMENT_CRUD.VERSION_LIST(payload)
    if (row.contentType === 'widget') return WIDGET_MANAGEMENT_CRUD.VERSION_LIST(payload)
    if (row.contentType === 'site-navigation') return SITE_NAVIGATION_CRUD.VERSION_LIST(payload)
    return null
  }

  const fetchVersionData = async (requestedData: any) => {
    try {
      return await mainApiService(requestedData)
    } catch (error) {
      logger.error(error)
      return
    }
  }
  const getContentType = (item: any) => {
    if (item?.widgetId) return 'widget'
    if (item?.containerId) return 'container'
    return 'siteNavigation'
  }

  const processResultData = async (data: any[]) => {
    let pagesRoutes: any
    let module: any
    if (data?.[0]?.widgetId) {
      pagesRoutes = PAGES_ROUTES.ManageWidget
    } else if (data?.[0]?.containerId) {
      pagesRoutes = PAGES_ROUTES.ManageContainer
      module = MODULE_CONSTANTS.CONTAINER
    } else {
      pagesRoutes = PAGES_ROUTES.SiteNavigation
    }

    const TRAY_RANKING_TOGGLE_COLUMN = [
      { id: 'isTrayRankingEnabled', label: 'Tray Ranking', sort: false },
    ]
    const hasTrayRankingStatus = columns?.some(
      (col: any) => col.id === 'trayRankingStatus' || col.id === 'isTrayRankingEnabled',
    )

    const columnHeader = hasTrayRankingStatus
      ? [...TRAY_RANKING_TOGGLE_COLUMN, ...(columns ?? [])]
      : columns

    const tempData = await getListingRowData({
      data: data,
      columnHeader: columnHeader,
      pageRoute: pagesRoutes,
      extraParams: ['_id'],
      module: module,
    })

    const displayData = tempData?.map((ele: any, index: any) => {
      const transformed = transformData(ele, data[index])
      if (hasTrayRankingStatus) {
        const apiValue =
          data[index]?.isTrayRankingEnabled === undefined
            ? ele.isTrayRankingEnabled
            : data[index].isTrayRankingEnabled
        const isTrayRankingEnabled = apiValue !== 'NA' && apiValue !== false
        const trayRankingDef = data[index]?.[containerTrayRankingDefinitionKey]
        const chipStatus =
          trayRankingDef === false || trayRankingDef === 'NA' ? 'unavailable' : isTrayRankingEnabled
        const trayRankingChip = (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <TrayRankingStatusChip status={chipStatus} />
          </div>
        )

        transformed.trayRankingStatus = trayRankingChip
        transformed.isTrayRankingEnabled = trayRankingChip
      }
      return transformed
    })
    return displayData
  }

  const transformData = (data: any, initialData: any) => {
    return {
      ...data,
      contentId: initialData.widgetId || initialData.containerId || initialData.siteNavigationId,
      title:
        initialData.widgetTitle ||
        initialData.containerTitle ||
        initialData.siteNavigationTitle ||
        initialData.title,
      contentType: getContentType(initialData),
    }
  }

  const displayValue = (value: any, columnValue: any) => {
    if (typeof value === 'string' || Array.isArray(value)) {
      return value ? (
        <TooltipText text={value.toString()} length={columnValue === 'title' ? 55 : 40} />
      ) : (
        'NA'
      )
    }
    if (typeof value === 'number') {
      return value
    }
    return value || 'NA'
  }

  const totalPages = Math.ceil(dataCount / Number(pageData.pageCount))
  const isLastPage =
    dataCount === 0 ||
    (dataCount > 0 && (dataCount <= pageData.pageCount || pageData.pageNumber === totalPages))

  const showChildVersions = (rowURL: any, ind: any) => {
    return selectedVersion.map((versionData: any, index: number) => {
      rowURL = versionData?.rowURL
      return (
        <TableRow
          component={rowURL ? Link : TableRow}
          to={rowURL ?? ''}
          key={ind}
          onMouseEnter={() => {
            setSelectedId(versionData?._id)
          }}
          onMouseLeave={() => {
            setSelectedId('')
          }}
          onClick={(e: any) => {
            e.stopPropagation()
            const path = rowURL?.split('/')
            const role = getRoleByURL(path[1])

            checkRolePermissionOnLinkClick(e, role, navigate)
            if (handleRowClick) handleRowClick(versionData)
          }}
          data-testid='table-row-click'
          sx={{
            borderTop: index === 0 ? '12px solid #f5f4f4' : '',
            borderBottom: index === selectedVersion.length - 1 ? '12px solid #f5f4f4' : '',
          }}
        >
          <TableCell
            style={{ cursor: 'pointer' }}
            className={`dashLine-cell ${selectedId === versionData._id ? 'bg-col4' : ''}`}
            align='left'
          >
            <span
              className={`${cloneProps?.isClonable ? '' : 'dashLine-clone'} ${
                index === 0 ? 'listing-dashLine-first' : 'listing-dashLine'
              }`}
            ></span>
          </TableCell>

          {columns?.map((column) => {
            const value = versionData[column.id]
            const shouldCopyAllowed =
              isCopyActionAllowed && copyableColumns.includes(String(column.id))
            return (
              <TableCell
                key={column.id}
                className={selectedId === versionData._id ? 'bg-col4' : ''}
              >
                {handleCellData(shouldCopyAllowed, value, column?.id)}
              </TableCell>
            )
          })}
        </TableRow>
      )
    })
  }

  const showNestedRows = (data: any[], level = 0) => {
    return data?.map((item, index) => {
      const rowURL = item?.url || item?.rowURL
      const isChild = Array.isArray(item.child) && item.child.length > 0
      return (
        <React.Fragment key={item.key}>
          <TableRow>
            <TableCell className='nested-table-parent-td'>
              <Table className='nested-table'>
                <TableBody>
                  <TableRow
                    component={rowURL ? Link : TableRow}
                    to={rowURL ?? ''}
                    onMouseEnter={() => setSelectedId(item.key)}
                    onMouseLeave={() => setSelectedId('')}
                    onClick={(e: any) => {
                      e.stopPropagation()
                      const path = rowURL?.split('/')
                      const role = getRoleByURL(path[1])
                      checkRolePermissionOnLinkClick(e, role, navigate)

                      if (handleRowClick) handleRowClick(item)
                    }}
                    data-testid='table-row-click'
                    sx={{
                      borderTop: index === 0 ? '12px solid #f5f4f4' : '',
                      borderBottom: isChild ? '12px solid #f5f4f4' : '',
                    }}
                  >
                    {Array.from({ length: level }).map((_, i) => (
                      <TableCell
                        key={`spacer-${level}-${i}`}
                        className='nested-empty-tb'
                      ></TableCell>
                    ))}
                    <TableCell
                      className={`dashLine-cell ${
                        selectedId === item.key ? 'bg-col4' : ''
                      } level-${level}`}
                      align='left'
                    >
                      <div className='dashLine'></div>
                    </TableCell>

                    <TableCell className={selectedId === item.key ? 'bg-col4' : ''}>
                      <div className={'paragraph-regular-6 interFont'}>{item.value}</div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableCell>
          </TableRow>

          {isChild && showNestedRows(item.child, level + 1)}
        </React.Fragment>
      )
    })
  }

  const handleProgressBar = async (row: any) => {
    if (selectedProgressBarId === row._id) {
      setShowProgressBar(!showProgressBar)
    } else {
      const payload = preparePayload(row)
      const requestedData = AUDIT_LOGS.CONTENT_STATUS(payload)
      const result = await mainApiService(requestedData)
      if (result?.responseCode === 200) {
        setProgressBarData(result?.data)
        setSelectedProgressBarId(row._id)
      } else {
        setProgressBarData({})
        setSelectedProgressBarId('')
      }
    }
  }

  const handleShowProgressBar = (ind: any) => {
    return (
      <TableRow
        key={ind}
        data-testid='table-row-click'
        sx={{
          borderTop: '12px solid #f5f4f4',
          borderBottom: '12px solid #f5f4f4',
        }}
      >
        <TableCell style={{ cursor: 'pointer' }} className={'dashLine-cell'} align='left'>
          <span className={'dashLine-clone-progressBar listing-dashLine-first progress-bar'}></span>
        </TableCell>
        <TableCell colSpan={columns?.length ?? 0 - 1} style={{ background: '#f5f4f4' }}>
          <ProgressBar progressBarData={progressBarData} />
        </TableCell>
      </TableRow>
    )
  }

  const filterContainer = (customClass?: string) => {
    if (!showFilterOptions) return null
    return (
      <div className={`listing-filter-container ${customClass}`}>
        {headerAddon && (filters.length > 0 || stateParam) && (
          <MUIButton
            label='Clear filters'
            handleClickCB={() => {
              setFilters([])
              setPageData((prev) => ({ ...prev, pageNumber: 1 }))
              removeSessionStorage('localFilters')
              isRetainFilters && setSessionStorage('localFilters', null, location.pathname)
              setLocalFiltersAndPageData(undefined, {
                localPageCount: pageData.pageCount,
                localPageNumber: 1,
                scrollPosition: 0,
              })(false)
              setClearFiltersTrigger((prev) => prev + 1)
              searchParams.delete('state')
              setSearchParams(searchParams, { replace: true })
              handleClearAllFilters?.()
            }}
            className='clear-filters-btn'
            type='button'
          />
        )}
        <div className='listing-filter-group'>
          {filterFields.map((item) => (
            <ListingFilter
              filters={filters.find((it: any) => it.filterBy === item.id)?.filterValue || []}
              key={item.id}
              searchable={item.searchable}
              lable={item.lable}
              id={item.id}
              options={item.options ?? []}
              searchFunction={handleFilterSubmit}
              isFilterSingleSelect={item?.isFilterSingleSelect ?? false}
              clearFilter={clearFilterValues}
              clearFilterTrigger={clearFiltersTrigger}
            />
          ))}
        </div>
      </div>
    )
  }
  const searchField = ({
    className = false,
    fullWidth = false,
    backgroundColor = 'transparent',
  }) => {
    if (!showSearchField) return null

    const searchBarSizeValue = searchBarSize === 'large' ? 'large' : ''
    const classNameValue = className
      ? `c-input listing-search ${searchBarSizeValue}${displayMode === 'screen' ? ' screen' : ''}`
      : ''
    return (
      <MUIFieldWithoutControl
        className={classNameValue}
        style={{ backgroundColor }}
        fullWidth={fullWidth}
        name='outlined-search'
        onChange={(e: any) => {
          if (e?.trim()?.length === 0 && setLastSearchFieldValue && className) {
            setLastSearchFieldValue('')
            isRetainFilters && setSearchParams({}, { replace: true })
          } else {
            isRetainFilters && setSearchParams({ search: e }, { replace: true })
          }
          setPageData((prev) => ({ ...prev, pageNumber: 1 }))
          setLocalFiltersAndPageData(undefined, {
            localPageCount: pageData.pageCount,
            localPageNumber: 1,
            scrollPosition: 0,
          })(false)
          setSearchInput(e)
          isRetainFilters && setSessionStorage('localSearchFilter', e, location.pathname)
        }}
        placeholder={searchPlaceholder}
        value={searchInput}
        label={searchLabel}
        inputProps={{ 'data-testid': replaceUnwanted(searchPlaceholder) }}
        autoComplete='off'
        showClearIcon
        InputProps={{
          id: 'searchId',
          startAdornment: (
            <>
              {showSearchIcon && (
                <InputAdornment position='start'>
                  <Search style={{ color: '#B1B4B6' }} />
                </InputAdornment>
              )}
            </>
          ),
        }}
      />
    )
  }
  const noDataTypography = (typographyStyle: CSSProperties, noDataText: string) => {
    return (
      <Typography
        className='d-flex justify-content-center'
        style={typographyStyle}
        data-testid={`div_${replaceUnwanted(tableTitle)}_no_data_found`}
      >
        {noDataText}
      </Typography>
    )
  }
  const noDataContainer = (typographyStyle: CSSProperties, noDataText: string) => {
    return (
      <div
        className={'d-flex justify-content-center align-items-center flex-column'}
        style={{ paddingTop: '7rem', height: '100%', paddingBottom: '5rem' }}
      >
        <div>
          <NoAssetIcon />
        </div>
        {noDataTypography(typographyStyle, noDataText)}
      </div>
    )
  }
  const displayNoDataContainer = () => {
    if (parentName?.length === 0)
      return !isLoading && noDataTypography({ padding: '50px 50px' }, noDataPlaceHolder)

    if (searchValue?.trim()?.length > 0)
      return (
        !isLoading &&
        noDataContainer(
          { padding: '0 50px', fontWeight: 600 },
          noSearchResultText || `No ${parentName || 'Data'} Found`,
        )
      )

    return (
      !isLoading &&
      noDataContainer(
        { padding: '0 50px', fontWeight: 'bold' },
        emptyStateText ?? 'Please initiate search to add.',
      )
    )
  }
  const displaySelectAllCheckbox = () => {
    const { checked, disabled } = selectAllCheckboxData
    return (
      <MUICheckbox
        id='selectAll'
        checked={checked}
        handleChange={() => onSelectAllChange?.(!checked)}
        disabled={disabled ?? false}
        label=''
        size='small'
        sx={{ paddingLeft: 1 }}
      />
    )
  }

  const getAdvancedFilterConditions = (columnDef: any, metaAttribute: any) => {
    const filterValueType = metaAttribute.find((col: { id: any }) => col.id === columnDef)?.dataType
    return getFilterOptions(filterValueType).map((item) => (
      <MenuItem key={item.label} value={item.value}>
        {item.label}
      </MenuItem>
    ))
  }
  const displayTableHeaderFirstColumn = () => {
    if (
      showVersionColumn ||
      isProgressBar ||
      cloneProps?.isClonable ||
      selectAllCheckboxData?.showCheckbox
    ) {
      if (selectAllCheckboxData?.showCheckbox)
        return <TableCell>{displaySelectAllCheckbox()}</TableCell>
      return <TableCell>{''}</TableCell>
    }
    return null
  }
  const displayVersionAndCloneModal = (row: any, isCloneDisabled: boolean) => {
    if (
      entityType === 'container' &&
      entryPoint !== PageNames.TrayRanking &&
      row?.isChildPageRequired?.toLowerCase() === 'yes'
    ) {
      return (
        <button
          className='listing-edit-icon'
          data-testid='container-child-edit-btn'
          onClick={(e) => {
            e.preventDefault()
            navigate(`/${PAGES_ROUTES.ManageContainer}/${PAGES_ROUTES.PageTypeView}/${row._id}`)
          }}
        >
          <EyeIcon />
        </button>
      )
    }

    if (entityType === 'container') {
      if (row?.isCloneRequired?.toLowerCase() === 'yes' && cloneProps?.isClonable) {
        return (
          <div className='clone-icons'>
            <CloneModal
              cloneProps={{
                redirectViewAPiRouteMidPoint: cloneProps.redirectViewAPiRouteMidPoint,
                cloneSegmemtIdNodeForApiPayload: cloneProps.cloneSegmemtIdNodeForApiPayload,
                apiMidPoint: cloneProps.apiMidPoint,
                titleNodeToDisplay: cloneProps.titleNodeToDisplay,
                title: cloneProps.title,
                isCloneDisabled: isCloneDisabled,
                openInNewTab: cloneProps.openInNewTab,
              }}
              id={row._id}
              onCancel={handleRefresh}
            />
          </div>
        )
      }

      return <div style={{ width: '28px' }}></div>
    }

    if (cloneProps?.isClonable) {
      return (
        <div className='clone-icons'>
          <CloneModal
            cloneProps={{
              redirectViewAPiRouteMidPoint: cloneProps.redirectViewAPiRouteMidPoint,
              cloneSegmemtIdNodeForApiPayload: cloneProps.cloneSegmemtIdNodeForApiPayload,
              apiMidPoint: cloneProps.apiMidPoint,
              titleNodeToDisplay: cloneProps.titleNodeToDisplay,
              title: cloneProps.title,
              isCloneDisabled: isCloneDisabled,
              openInNewTab: cloneProps.openInNewTab,
              layout: row?.layout,
            }}
            id={row?.jobId ?? row._id}
            onCancel={handleRefresh}
          />
        </div>
      )
    }

    return null
  }
  const displayVersionAndCloneColumn = (row: any, index: number, isCloneDisabled: boolean) => {
    if (!showVersionColumn && !cloneProps?.isClonable) return null
    return (
      <TableCell
        className={selectedId === index ? 'bg-col4' : ''}
        sx={{ paddingRight: '22px !important' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`version-clone-icons ${showVersionColumn ? '' : 'flex-start'}`}>
          {showVersionColumn && row?.versionCount > 1 && (
            <button
              className='version-icon'
              data-testid='cloneBtn'
              onClick={(e) => {
                e.preventDefault()
                handleVersionIconClick(row)
              }}
            >
              <VersionIcon />
              <span className='version-count'>{row?.versionCount}</span>
            </button>
          )}

          {displayVersionAndCloneModal(row, isCloneDisabled)}
        </div>
      </TableCell>
    )
  }

  const parentDivPadding = () => {
    if (displayMode === 'screen') return '20px 0'
    if (!isAssociatedList && !noPadding) return '20px 24px'
    return '0 0 20px 0'
  }

  return (
    <MUIGrid
      className={`interFont listing-container ${listingContainerParentClassName}`}
      style={{
        width: '100%',
        backgroundColor: displayMode === 'screen' ? '#fff' : '#f5f4f4',
        padding: parentDivPadding(),
      }}
    >
      <Grid className='interFont c-table'>
        {isAssociatedList ? (
          <div
            style={{
              height: `${showFilterOptions ? '8rem' : '6rem'}`,
              backgroundColor: '#F7F8F8',
            }}
          >
            {filterContainer('associated-listing-filter-container')}
            <div style={{ paddingTop: `${showFilterOptions ? '0.5rem' : '1.5rem'}` }}>
              {searchField({ fullWidth: true, backgroundColor: 'white' })}
            </div>
          </div>
        ) : (
          <>
            <MUIGrid
              container
              className={`d-flex justify-content-between align-items-center table-title-search-container ${
                searchFullWidth ? 'search-full-width' : ''
              }`}
            >
              <MUIGrid item md={6} lg={6}>
                {showBackButton && (
                  <BackButton
                    label={`${tableTitle}`}
                    className='col73'
                    handleOnClick={handelBackButton}
                  />
                )}
                {!showBackButton && tableTitle && <p className='listing__title'>{tableTitle}</p>}
              </MUIGrid>

              <MUIGrid item md={searchFullWidth ? 12 : 6} className={searchClassName}>
                <MUIGrid container className='d-flex justify-content-end align-items-center'>
                  <MUIGrid direction='row' className='search-container' gap={3} display='flex'>
                    {searchField({ className: true })}
                    {showCreateButton && showTable && (
                      <MUIButton
                        id={replaceUnwanted(
                          headerBtnLabel === 'Add new' ? createButtonLabel : headerBtnLabel,
                        )}
                        className='create__btn'
                        variant={headerBtnVariant}
                        label={headerBtnLabel === 'Add new' ? createButtonLabel : headerBtnLabel}
                        type='button'
                        data-testid='create-btn-click'
                        handleClickCB={createButtonClick}
                        disabled={createButtonDisabled}
                      />
                    )}
                  </MUIGrid>
                </MUIGrid>
              </MUIGrid>
            </MUIGrid>
            {showHeaderInfo && (
              <MUIGrid container className='d-flex justify-content-between align-items-center'>
                {showHeaderInfo}
              </MUIGrid>
            )}
            {filterContainer()}
          </>
        )}

        {showTable && (
          <>
            {isActiveWindow.length > 0 && (
              <div className='revoke-access-message' style={{ marginTop: '1rem' }}>
                <Caution />
                <span>{isActiveWindow}</span>
              </div>
            )}
            <TableContainer
              data-testid='listing__table'
              className={`border-6 shadow-table ${
                isAssociatedList || isActiveWindow.length > 0 ? 'mt-1' : 'table-container'
              }`}
              sx={{
                height: '100%',
                backgroundColor: '#fff',
                filter:
                  displayMode === 'screen'
                    ? 'drop-shadow(0 20px 24px rgba(56, 63, 67, 0.08))'
                    : 'none',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    {displayTableHeaderFirstColumn()}
                    {headerColumns?.map((column: any) => {
                      return (
                        <TableCell key={column.id} align='center'>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              alignContent: 'space-between',
                              fontSize: '12px',
                              fontWeight: 'bold',
                            }}
                          >
                            <div className='interFont'>{column.label}</div>
                            {(column.sort || column.showFilter) && (
                              <button
                                data-testid='openfilter'
                                id='openFilter'
                                className='m-4 d-flex align-items-center border-none transparent-background'
                                onClick={(event) => {
                                  handleSortIconClick(event, column.id)
                                }}
                              >
                                {checkIfFilterApplied(column.id) && column.showFilter ? (
                                  <SortIcon
                                    style={{
                                      fontSize: '18px',
                                      marginLeft: '7px',
                                    }}
                                  />
                                ) : (
                                  <MoreVertOutlined
                                    style={{
                                      fontSize: '18px',
                                      fill:
                                        column.id === selectedCol.selectedCol
                                          ? '#402080'
                                          : '#b1b4b6',
                                    }}
                                  />
                                )}
                              </button>
                            )}
                            {column.id === colData.selectedCol && (
                              <Menu
                                className='border-12 c-sort'
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={() => onMenuClose(true)}
                                data-testid='sort-dialog'
                              >
                                <div className='d-flex justify-content-between align-items-center'>
                                  <Typography>Sort By</Typography>
                                  {showAdvanceFilter && column.showFilter && (
                                    <button
                                      className={`border-none transparent-background ${
                                        checkIfFilterApplied(colData.selectedCol)
                                          ? 'clear-all'
                                          : 'clear-all-disabled'
                                      }`}
                                      data-testid='clear-all-button'
                                      onClick={() => {
                                        clearFilter(colData.selectedCol)
                                        if (pageData.pageNumber > 1)
                                          setPageData((prev) => ({ ...prev, pageNumber: 1 }))
                                      }}
                                    >
                                      Clear Filter
                                    </button>
                                  )}
                                </div>
                                {column.sort && (
                                  <RadioGroup
                                    aria-label='options'
                                    name='options'
                                    value={colData.selectedOptionType}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                      setColData({
                                        ...colData,
                                        selectedOptionType: Number.parseInt(event.target.value),
                                      })
                                      setAnchorEl(null)
                                      setShouldListApiCall(true)
                                      onMenuClose()
                                      setSelectedCol({
                                        ...colData,
                                        selectedOptionType: Number.parseInt(event.target.value),
                                      })
                                    }}
                                  >
                                    <MenuItem
                                      sx={{
                                        paddingLeft: 0,
                                      }}
                                      className='d-flex justify-content-between align-items-center'
                                    >
                                      <FormControlLabel
                                        labelPlacement='start'
                                        value={1}
                                        label={
                                          <div className='d-flex w-100 align-item-center justify-content-center'>
                                            <div className='d-flex align-items-center'>
                                              <SortByAlpha style={{ fontSize: '18px' }} />
                                            </div>
                                            <div className='ml-8p'>
                                              <span className='font-12p'>Ascending</span>
                                            </div>
                                          </div>
                                        }
                                        control={<Radio className='ml-auto' />}
                                      />
                                    </MenuItem>
                                    <MenuItem
                                      sx={{
                                        paddingLeft: 0,
                                      }}
                                      className='d-flex justify-content-between align-items-center'
                                    >
                                      <FormControlLabel
                                        labelPlacement='start'
                                        value={-1}
                                        label={
                                          <div className='d-flex w-100 align-item-center justify-content-center'>
                                            <div className='d-flex align-items-center'>
                                              <DescendingIcon />
                                            </div>
                                            <div className='ml-8p'>
                                              <span className='font-12p'>Descending</span>
                                            </div>
                                          </div>
                                        }
                                        control={<Radio />}
                                      />
                                    </MenuItem>
                                  </RadioGroup>
                                )}
                                {showAdvanceFilter && column.showFilter && (
                                  <div>
                                    <MUIButton
                                      width={150}
                                      fontSize={14}
                                      color='secondary'
                                      height='40px'
                                      type='button'
                                      data-testid='filter-button'
                                      label={'Filter'}
                                      variant='text'
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                      }}
                                      startIcon={<FilterListIcon sx={{ marginRight: '8px' }} />}
                                      endIcon={
                                        <LastPageIcon sx={{ fontSize: '20px !important' }} />
                                      }
                                      handleClickCB={openFilterInSort}
                                      nonDisableProp
                                    />
                                    {openFilter && (
                                      <Menu
                                        className='border-12 c-sort'
                                        anchorEl={anchorElFilter}
                                        open={Boolean(anchorElFilter)}
                                        onClose={closeFilterInSort}
                                        data-testid='filter-value'
                                        anchorOrigin={{
                                          vertical: 'top',
                                          horizontal: 'right',
                                        }}
                                        transformOrigin={{
                                          vertical: 'top',
                                          horizontal: 'left',
                                        }}
                                        slotProps={{
                                          paper: {
                                            style: {
                                              width:
                                                column?.dataType.toLowerCase() === 'date'
                                                  ? '310px'
                                                  : '245px',
                                              maxWidth: '400px',
                                              overflow: 'auto',
                                            },
                                          },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            fontWeight: 'bold',
                                            marginBottom: '18px',
                                          }}
                                        >
                                          Filter
                                        </Box>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                            height: '170px',
                                          }}
                                        >
                                          <FormControl fullWidth>
                                            <InputLabel id='demo-simple-select-label'>
                                              Condition
                                            </InputLabel>
                                            <Select
                                              labelId='demo-simple-select-helper-label'
                                              id='demo-simple-select-helper'
                                              data-testid='condition-filter'
                                              value={condition}
                                              label='Condition'
                                              onChange={handleConditionChange}
                                            >
                                              {getAdvancedFilterConditions(column.id, columns)}
                                            </Select>
                                          </FormControl>
                                          {column?.dataType.toLowerCase() === 'date' ? (
                                            <div data-testid='date-input'>
                                              <MUIDateTimePicker
                                                name={'StartDate'}
                                                label={'Value'}
                                                type={'datetime'}
                                                onChange={handleFilterDateChange}
                                                value={selectedDateTime}
                                                views={CONSTANTS.DATE_TIME_FORMAT_VIEWS}
                                                error={{}}
                                              />
                                            </div>
                                          ) : (
                                            <TextField
                                              id='outlined-basic'
                                              label='Value'
                                              variant='outlined'
                                              value={filterInput}
                                              onChange={handleFilterInputChange}
                                              onKeyDown={(e: any) => {
                                                e.stopPropagation()
                                              }}
                                              inputProps={{ 'data-testid': 'filter-input' }}
                                            />
                                          )}
                                          <MUIButton
                                            width={150}
                                            fontSize={14}
                                            height='44px'
                                            type='button'
                                            label={'Apply'}
                                            variant='contained'
                                            handleClickCB={() => handleApply(column)}
                                            disabled={!(filterInput || selectedDateTime)}
                                            nonDisableProp
                                          />
                                        </Box>
                                      </Menu>
                                    )}
                                  </div>
                                )}
                              </Menu>
                            )}
                          </div>
                        </TableCell>
                      )
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!isLoading &&
                    tableRows?.length > 0 &&
                    tableRows?.map((row, ind) => {
                      // for masters URL is already available in CONSTANTS
                      const url: string = row?.url ?? ''
                      const id = getDynamicRowValue(row, 'id')
                      const disableRadioCheckBox = row?.checkbox?.props?.className?.includes(
                        'replace-asset-radio disabled-row',
                      )
                      const rowToDisable =
                        row?.checkbox?.props?.disabled ||
                        row?.checkbox?.props?.children?.[0]?.props?.disabled ||
                        disableRadioCheckBox
                      // for everything else passing from the parent component
                      const rowURL: string = row?.rowURL ?? url ?? ''
                      const isCloneDisabled = row[cloneToDisable] === 'NA'
                      return (
                        <>
                          <TableRow
                            component={rowURL ? Link : TableRow}
                            to={rowURL || ''}
                            state={advancedSearch ? { advancedSearch: true } : undefined}
                            key={row.id}
                            className={`table-link ${isRowClickable ? 'cursor - pointer' : ''} ${
                              rowToDisable ? 'disabled-row' : ''
                            } ${initialCheckedState?.[id] ? 'selected-row' : ''}`}
                            onMouseEnter={() => {
                              setSelectedId(ind)
                            }}
                            onMouseLeave={() => {
                              setSelectedId('')
                            }}
                            onClick={(e: any) => {
                              e.stopPropagation()
                              const path = rowURL?.split('/')
                              const role = getRoleByURL(path[1])
                              checkRolePermissionOnLinkClick(e, role, navigate)
                              setLocalFiltersAndPageData(undefined, {
                                localPageCount: pageData.pageCount,
                                localPageNumber: pageData.pageNumber,
                                scrollPosition: `${location.pathname}-${ind}`,
                              })(false)

                              if (rowURL && advancedSearch)
                                navigate(rowURL, { state: { advancedSearch: true } })

                              if (handleRowClick) handleRowClick(row)
                            }}
                            data-testid='table-row-click'
                            id={`${location.pathname}-${ind}`}
                          >
                            {displayVersionAndCloneColumn(row, ind, isCloneDisabled)}

                            {isProgressBar ? (
                              <TableCell
                                style={{ cursor: 'pointer' }}
                                className={selectedId === ind ? 'bg-col4' : ''}
                                sx={{ paddingRight: '22px !important' }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div
                                  className={`version-clone-icons ${
                                    isProgressBar ? '' : 'flex-start'
                                  }`}
                                >
                                  <button
                                    className='version-icon'
                                    data-testid='progress-bar-icon'
                                    onClick={(e) => {
                                      e.preventDefault()
                                      handleProgressBar(row)
                                    }}
                                  >
                                    <ProgressBarIcon />
                                  </button>
                                </div>
                              </TableCell>
                            ) : null}

                            {columns?.map((column) => {
                              const value = row[column.id]
                              const isThisCopyable =
                                isCopyActionAllowed && copyableColumns.includes(String(column.id))
                              return (
                                <TableCell
                                  key={column.id}
                                  className={selectedId === ind ? 'bg-col4' : ''}
                                >
                                  {handleCellData(isThisCopyable, value, column?.id)}
                                </TableCell>
                              )
                            })}
                          </TableRow>

                          {row?.child &&
                            Array.isArray(row?.child) &&
                            row?.child.length > 0 &&
                            isNestedRows &&
                            showNestedRows(row?.child)}

                          {showVersions &&
                            selectedVersionId === row?._id &&
                            showChildVersions(url, ind)}

                          {showProgressBar &&
                            selectedProgressBarId === row?._id &&
                            handleShowProgressBar(ind)}
                        </>
                      )
                    })}
                </TableBody>
              </Table>

              {isLoading && (
                <div
                  className='d-flex justify-content-center'
                  style={{ padding: '100px 0' }}
                  data-testid='circular-loader'
                >
                  <CircularProgress variant='indeterminate' />
                </div>
              )}
              {tableRows.length === 0 && displayNoDataContainer()}
            </TableContainer>
            <MUIGrid
              container
              className={`d-flex align-items-center mt-2 c-pagination ${
                dataCount === 0 ? 'justify-content-end' : 'justify-content-between'
              }`}
            >
              {dataCount > 0 && (
                <MUIGrid item direction='row' className='d-flex align-items-center col1'>
                  <SimpleDropdown
                    selectId='pageId'
                    options={paginationOption}
                    handleChange={handlePerPageView}
                    value={pageData.pageCount}
                  />

                  <Typography paddingX={3}>
                    Showing {pageData.pageCount > dataCount ? dataCount : tableRows.length} of{' '}
                    {dataCount} records
                  </Typography>
                </MUIGrid>
              )}
              <MUIGrid item>
                {isLastPage && onFetchOlderRecords && !hideFetchOlderRecords && (
                  <button
                    className='fs12 btn-fetching-data col1 ml-auto mb-1'
                    onClick={onFetchOlderRecords}
                  >
                    <span className='fs12 fetch-refresh-icon'>↻</span>
                    {'   '}
                    <span className='text-underline'>Fetch older data</span>
                  </button>
                )}
                {tableRows.length > 0 && dataCount > 0 && (
                  <MUIPagination
                    testId='userPagination'
                    onChange={tablePaginationChange}
                    count={Math.ceil(dataCount / Number(pageData.pageCount))}
                    page={pageData.pageNumber}
                  />
                )}
              </MUIGrid>
            </MUIGrid>
          </>
        )}
      </Grid>
    </MUIGrid>
  )
}

export default memo(ListingComponent)
